import React from 'react';
import { Box, Typography, Popover, Radio, RadioGroup, FormControlLabel, Button, Divider } from '@mui/material';
import { ArrowDownAZ, ArrowUpAZ } from 'lucide-react';
import Dropdown from '../ui/dropdown';
import CalendarInput from '../ui/calendar-input';
import dayjs from 'dayjs';
import { FilterParams } from '../transactions-table';

const dropdownOptions = [
    { value: 'transactions', label: 'Transactions' },
    { value: 'day', label: 'Day' },
    { value: 'week', label: 'Week' },
    { value: 'month', label: 'Month' },
    { value: 'quarter', label: 'Quarter' },
    { value: 'year', label: 'Year' }
];

// dropdown for relative date ranges centered on today
const relativeOptions = [
    { value: 'week', label: 'Last week to next week' },
    { value: 'month', label: 'Last month to next month' },
    { value: 'quarter', label: 'Last quarter to next quarter' },
    { value: 'year', label: 'Last year to next year' },
];

export type DateFilterHandle = {
    applyNow: () => void;
    clear: () => void;
};

interface DateFilterProps {
    anchorEl?: HTMLElement | null;
    open?: boolean;
    onClose?: () => void;
    onApply: (params: FilterParams) => React.SetStateAction<void>;
    // Renders the same sort/view/range controls inline (no Popover/anchor)
    // for use inside a combined modal instead of an anchored popup.
    embedded?: boolean;
    // Hides this section's own Apply Now/Clear buttons - used when a
    // parent (the combined filter modal) drives Apply/Clear for every
    // section at once via the imperative handle below instead.
    hideActions?: boolean;
    currentSort?: {
        sortBy?: string;
        sortDirection?: 'ASC' | 'DESC';
    };
    // New optional props to allow date-column popover to control header filters
    viewBy?: string;
    setViewBy?: (value: string) => void;
    unitValue?: string;
    setUnitValue?: (value: string) => void;
    isDateRange?: boolean;
    setIsDateRange?: (value: boolean) => void;
    startDate?: Date;
    endDate?: Date;
    setStartDate?: (value: Date) => void;
    setEndDate?: (value: Date) => void;
    companyStatus?: {
        earliestTransaction?: string | Date | null;
        latestTransaction?: string | Date | null;
        // Bank-filter-aware earliest record (unlike earliestTransaction,
        // which is deliberately entity-wide/unfiltered - see its comment in
        // transaction-controller.js) - the same reference point that drives
        // the "Start of Records" row indicator in the table itself, so the
        // Earliest Record button actually lands on what's visibly the first
        // row when a bank filter is active.
        startOfRecords?: { guid?: string | null; date?: string | Date | null } | null;
        [key: string]: any;
    };
}

const DateFilter = React.forwardRef<DateFilterHandle, DateFilterProps>(({
    anchorEl, open, onClose,
    onApply,
    currentSort,
    viewBy,
    setViewBy,
    unitValue,
    setUnitValue,
    isDateRange,
    setIsDateRange,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    companyStatus,
    embedded = false,
    hideActions = false,
}, ref) => {
    const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc' | ''>(
        currentSort?.sortBy === 'date' ? (currentSort?.sortDirection === 'ASC' ? 'asc' : 'desc') : ''
    );

    // track which relative range option user has selected (default to month)
    const [relativeRange, setRelativeRange] = React.useState<string>('month');

    // Local state for temporary changes (not applied until user clicks Apply)
    const [tempViewBy, setTempViewBy] = React.useState<string>(viewBy || '');
    const [tempUnitValue, setTempUnitValue] = React.useState<string>(unitValue || '');
    const [tempIsDateRange, setTempIsDateRange] = React.useState<boolean>(isDateRange || false);
    const [tempStartDate, setTempStartDate] = React.useState<Date | undefined>(startDate);
    const [tempEndDate, setTempEndDate] = React.useState<Date | undefined>(endDate);

    // placeholder text for dropdown: show "Custom" whenever a manual date range is active
    const dropdownPlaceholder = tempIsDateRange ? 'Custom' : '-';

    // Embedded mode has no anchored open/close lifecycle - treat it as
    // always "open" so the temp-state sync effects below still run.
    const effectiveOpen = embedded || open;

    // Sync props into local state when popover opens or props change
    React.useEffect(() => {
        if (effectiveOpen) {
            // Reset temp state to current props when popover opens
            setTempViewBy(viewBy || '');
            setTempUnitValue(unitValue || '');
            setTempIsDateRange(isDateRange || false);
            setTempStartDate(startDate);
            setTempEndDate(endDate);
        }
    }, [effectiveOpen, viewBy, unitValue, isDateRange, startDate, endDate]);

    // Sync props into sort state when they change
    React.useEffect(() => {
        setSortOrder(currentSort?.sortBy === 'date' ? (currentSort?.sortDirection === 'ASC' ? 'asc' : 'desc') : '');
    }, [currentSort]);

    // when the dropdown is shown, ensure we have a default or use the unitValue keyword
    React.useEffect(() => {
        if (effectiveOpen && !relativeRange && !tempIsDateRange) {
            if (tempUnitValue && ['week','month','quarter','year'].includes(tempUnitValue)) {
                setRelativeRange(tempUnitValue);
            } else {
                setRelativeRange('month');
            }
        }
    }, [effectiveOpen, relativeRange, tempIsDateRange, tempUnitValue]);

    // keep selection in sync with unitValue prop when it's a keyword
    React.useEffect(() => {
        if (!tempIsDateRange && tempUnitValue && ['week','month','quarter','year'].includes(tempUnitValue)) {
            setRelativeRange(tempUnitValue);
        }
    }, [tempUnitValue, tempIsDateRange]);

    // determine which relative range corresponds to supplied start/end dates
    React.useEffect(() => {
        // if unitValue is a keyword we already know the selection; don't override
        if (!tempIsDateRange && tempUnitValue && ['week','month','quarter','year'].includes(tempUnitValue)) {
            setRelativeRange(tempUnitValue);
            return;
        }
        if (tempIsDateRange) {
            // manual date range -> clear dropdown selection
            setRelativeRange('');
            return;
        }
        if (tempStartDate && tempEndDate) {
            const now = new Date();
            const sameDay = (d1: Date, d2: Date) => d1.toDateString() === d2.toDateString();

            let matched = '';
            // week: 7 days
            {
                const s = new Date(now);
                s.setDate(s.getDate() - 7);
                const e = new Date(now);
                e.setDate(e.getDate() + 7);
                if (sameDay(s, tempStartDate) && sameDay(e, tempEndDate)) matched = 'week';
            }
            // month: 1 month
            if (!matched) {
                const s = new Date(now);
                s.setMonth(s.getMonth() - 1);
                const e = new Date(now);
                e.setMonth(e.getMonth() + 1);
                if (sameDay(s, tempStartDate) && sameDay(e, tempEndDate)) matched = 'month';
            }
            if (!matched) {
                const s = new Date(now);
                s.setMonth(s.getMonth() - 3);
                const e = new Date(now);
                e.setMonth(e.getMonth() + 3);
                if (sameDay(s, tempStartDate) && sameDay(e, tempEndDate)) matched = 'quarter';
            }
            if (!matched) {
                const s = new Date(now);
                s.setFullYear(s.getFullYear() - 1);
                const e = new Date(now);
                e.setFullYear(e.getFullYear() + 1);
                if (sameDay(s, tempStartDate) && sameDay(e, tempEndDate)) matched = 'year';
            }

            setRelativeRange(matched as string);
        }
    }, [tempStartDate, tempEndDate, tempIsDateRange, tempUnitValue]);

    const handleClear = () => {
        setSortOrder('');
        setRelativeRange('month');
        // Only clear date-specific sort
        onApply({
            sortBy: undefined,
            sortDirection: 'DESC',
        });
    }

    const handleApplyNow = () => {
        // Apply all temp state changes
        if (setViewBy && tempViewBy !== viewBy) {
            setViewBy(tempViewBy);
        }
        if (setUnitValue && tempUnitValue !== unitValue) {
            setUnitValue(tempUnitValue);
        }
        if (setIsDateRange && tempIsDateRange !== isDateRange) {
            setIsDateRange(tempIsDateRange);
        }
        if (setStartDate && tempStartDate !== startDate) {
            setStartDate(tempStartDate!);
        }
        if (setEndDate && tempEndDate !== endDate) {
            setEndDate(tempEndDate!);
        }

        const params: any = {};
        if (sortOrder) {
            params.sortBy = 'date';
            params.sortDirection = (sortOrder.toUpperCase() as 'ASC' | 'DESC');
        }
        onApply(params);
    };

    React.useImperativeHandle(ref, () => ({
        applyNow: handleApplyNow,
        clear: handleClear,
    }));

    const content = (
        <>
            {!embedded && <>
                <Typography sx={{ fontWeight: 700, fontSize: 18 }}>Sort, View & Filter</Typography>
                <Divider sx={{ my: 1 }}/>
            </>}

            {/* Sort controls (existing) */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Typography sx={{ fontSize: 14 }}>Sort By Date</Typography>
                </Box>
            </Box>
            <RadioGroup value={sortOrder || ''} onChange={e => setSortOrder(e.target.value as 'asc' | 'desc')} sx={{ mb: 1, pl: 3 }}>
                <FormControlLabel
                    value="desc"
                    sx={{ height: 24 }}
                    control={<Radio size='small'/>}
                    label={<Typography sx={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 1 }}><ArrowUpAZ size={14} />Sort newest to oldest</Typography>}
                />
                <FormControlLabel
                    value="asc"
                    sx={{ height: 24 }}
                    control={<Radio size='small'/>}
                    label={<Typography sx={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 1 }}><ArrowDownAZ size={14} />Sort oldest to newest</Typography>}
                />
            </RadioGroup>
            <Divider sx={{ my: 1 }}/>
            {/* View By (moved from header) */}
            {typeof setViewBy === 'function' && (
                <Box mb={0.5} display="flex" justifyContent="space-between" alignItems="center" gap={2}>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>View By</Typography>
                    <Dropdown
                        options={dropdownOptions}
                        value={tempViewBy}
                        onChange={setTempViewBy}
                        height={34}
                        borderRadius={1}
                        InputProps={{ style: { fontSize: 13 } }}
                        width={240}
                    />
                </Box>
            )}
            <Divider sx={{ my: 1 }}/>
            {/* Unit / Date range (moved from header) */}
            {(typeof setUnitValue === 'function' || typeof setStartDate === 'function') && (
                <Box display="flex" mb={1} flexDirection="column" gap={0.5}>
                    {typeof setUnitValue === 'function' && (
                        <Box sx={{ flex: 1 }} display="flex" alignItems="center" gap={2} justifyContent="space-between">
                            <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 0.5 }}>Date Range</Typography>
                            <Dropdown
                                options={relativeOptions}
                                value={relativeRange}
                                placeholder={dropdownPlaceholder}
                                onChange={val => {
                                    setRelativeRange(val);
                                    // compute start/end based on current date
                                    const now = new Date();
                                    let s = new Date(now);
                                    let e = new Date(now);
                                    switch (val) {
                                        case 'week':
                                            s.setDate(s.getDate() - 7);
                                            e.setDate(e.getDate() + 7);
                                            break;
                                        case 'month':
                                            s.setMonth(s.getMonth() - 1);
                                            e.setMonth(e.getMonth() + 1);
                                            break;
                                        case 'quarter':
                                            s.setMonth(s.getMonth() - 3);
                                            e.setMonth(e.getMonth() + 3);
                                            break;
                                        case 'year':
                                            s.setFullYear(s.getFullYear() - 1);
                                            e.setFullYear(e.getFullYear() + 1);
                                            break;
                                        default:
                                            break;
                                    }
                                    // Store in temp state, don't apply yet
                                    setTempStartDate(s);
                                    setTempEndDate(e);
                                    setTempIsDateRange(false);
                                    setTempUnitValue(val);
                                }}
                                height={34}
                                borderRadius={1}
                                InputProps={{ style: { fontSize: 13 } }}
                                width={240}
                            />
                        </Box>
                    )}
                    {typeof setStartDate === 'function' && (
                        <Box sx={{ flex: 1, boxSizing: 'border-box', gap: 0.5 }} display="flex" flexDirection="column">
                            {/* Start and end each have their own date picker
                                (instead of one shared calendar switching between
                                them). An end before the start is pulled up to it. */}
                            <Box display="flex" gap={1}>
                                <CalendarInput
                                    label={<Typography sx={{ fontSize: 12, mb: 0.25, color: 'var(--color-text-muted)' }}>Start date</Typography>}
                                    value={dayjs(tempStartDate || new Date()).format('YYYY-MM-DD')}
                                    onChange={(iso) => {
                                        // Store in temp state, don't apply yet
                                        const s = dayjs(iso).toDate();
                                        setTempStartDate(s);
                                        if (tempEndDate && tempEndDate < s) setTempEndDate(s);
                                        setTempIsDateRange(true);
                                        setTempUnitValue('');
                                        setRelativeRange('');
                                    }}
                                    sx={{ '& .MuiInputBase-root': { height: 30, fontSize: 13 } }}
                                />
                                <CalendarInput
                                    label={<Typography sx={{ fontSize: 12, mb: 0.25, color: 'var(--color-text-muted)' }}>End date</Typography>}
                                    value={dayjs(tempEndDate || new Date()).format('YYYY-MM-DD')}
                                    minDate={dayjs(tempStartDate || new Date()).format('YYYY-MM-DD')}
                                    onChange={(iso) => {
                                        // Store in temp state, don't apply yet
                                        setTempEndDate(dayjs(iso).toDate());
                                        if (!tempStartDate) setTempStartDate(new Date());
                                        setTempIsDateRange(true);
                                        setTempUnitValue('');
                                        setRelativeRange('');
                                    }}
                                    sx={{ '& .MuiInputBase-root': { height: 30, fontSize: 13 } }}
                                />
                            </Box>
                            {typeof setStartDate === 'function' && companyStatus && (
                                <Box display="flex" gap={1}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        fullWidth
                                        sx={{ 
                                            height: 24,
                                            fontSize: 10,
                                            borderColor: '#E0E0E0',
                                            color: 'var(--color-text-muted)',
                                            '&:hover': { 
                                                borderColor: 'var(--color-primary)',
                                                backgroundColor: '#F0F9F7'
                                            }
                                        }}
                                        onClick={() => {
                                            // Prefer startOfRecords - it respects the active bank
                                            // filter and matches what the table itself marks as the
                                            // first row; earliestTransaction is entity-wide and can
                                            // point at a date with nothing visible under the current
                                            // filter. Fall back to it only when startOfRecords hasn't
                                            // loaded yet.
                                            const earliestSource = companyStatus?.startOfRecords?.date ?? companyStatus?.earliestTransaction;
                                            if (earliestSource) {
                                                const earliestDate = typeof earliestSource === 'string'
                                                    ? new Date(earliestSource)
                                                    : earliestSource;
                                                setTempStartDate(earliestDate);
                                                setTempIsDateRange(true);
                                                setTempUnitValue('');
                                                setRelativeRange('');
                                            }
                                        }}
                                        disabled={!companyStatus?.startOfRecords?.date && !companyStatus?.earliestTransaction}
                                    >
                                        Earliest Record
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        fullWidth
                                        sx={{ 
                                            height: 24,
                                            fontSize: 10,
                                            borderColor: '#E0E0E0',
                                            color: 'var(--color-text-muted)',
                                            '&:hover': { 
                                                borderColor: 'var(--color-primary)',
                                                backgroundColor: '#F0F9F7'
                                            }
                                        }}
                                        onClick={() => {
                                            if (companyStatus?.latestTransaction) {
                                                const latestDate = typeof companyStatus.latestTransaction === 'string'
                                                    ? new Date(companyStatus.latestTransaction)
                                                    : companyStatus.latestTransaction;
                                                setTempEndDate(latestDate);
                                                setTempIsDateRange(true);
                                                setTempUnitValue('');
                                                setRelativeRange('');
                                            }
                                        }}
                                        disabled={!companyStatus?.latestTransaction}
                                    >
                                        Latest Record
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
            )}

            {!hideActions && (
                <Box display="flex" gap={1} mt={2}>
                    <Button
                        fullWidth
                        variant="contained"
                        sx={{ background: 'var(--color-primary)', color: '#fff', borderRadius: 2, fontWeight: 600, boxShadow: 'none', '&:hover': { background: 'var(--color-primary)' } }}
                        onClick={handleApplyNow}
                    >
                        Apply Now
                    </Button>
                    <Button
                        fullWidth
                        variant="contained"
                        sx={{ background: 'var(--color-container-background)', color: 'var(--color-text-muted)', borderRadius: 2, fontWeight: 600, boxShadow: 'none', '&:hover': { background: 'var(--color-container-background)' } }}
                        onClick={handleClear}
                    >
                        Clear
                    </Button>
                </Box>
            )}
        </>
    );

    if (embedded) {
        return <Box>{content}</Box>;
    }

    return (
        <Popover
            open={!!open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            PaperProps={{ sx: { borderRadius: 3, minWidth: 320, p: 2, boxShadow: 3 } }}
        >
            {content}
        </Popover>
    );
});

export default DateFilter;
